import React, { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import SingleCustomAutocomplete from "src/Components/CustomAutocomplete/SingleCustomAutocomplete";
import { generateForm } from "src/helpers/functions";
import axios from "axios";
import { toast } from "react-toastify";

const StatusAddUpdateForm = ({ visible, handleClose, jobId, reloadStatuses }) => {
  const [saveIsLoading, setSaveIsLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [formData, setFormData] = useState({
    job_id: jobId,
    status_id: null,
    date: "",
    description: "",
    id: -1,
  });

  const resetForm = () => {
    setFormData({
      job_id: jobId,
      status_id: null,
      date: "",
      description: "",
      id: -1,
    });
  };

  const getStatuses = () => {
    axios
      .get(`${process.env.REACT_APP_API}/admin/statuses/get`, {})
      .then(function (response) {
        setStatuses(response.data);
      })
      .catch(function (error) {
        toast("Error", {
          position: "top-right",
          type: "error",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        console.log(error);
      });
  };

  const customOnChange = (newValue, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleInputChange = (e) => {
    e.preventDefault();
    const { id, value, checked, type } = e.target;
    if (type === "checkbox") {
      setFormData({
        ...formData,
        [id]: checked,
      });
    } else {
      setFormData({
        ...formData,
        [id]: value,
      });
    }
  };

  const saveForm = () => {
    setSaveIsLoading(true);
    if (formData.id > 0) {
      const submitFormData = generateForm(formData);
      axios({
        method: "post",
        url: `${process.env.REACT_APP_API}/admin/jobs/status/update`,
        data: submitFormData,
      })
        .then((response) => {
          toast.success(`Updated successfully`);
          setSaveIsLoading(false);
          handleClose();
          if (reloadStatuses) reloadStatuses();
        })
        .catch((error) => {
          setSaveIsLoading(false);
          toast.error(`Error While Saving`);
        });
    } else {
      const submitFormData = generateForm(formData);
      axios({
        method: "post",
        accept: "any",
        url: `${process.env.REACT_APP_API}/admin/jobs/status/add`,
        data: submitFormData,
      })
        .then((response) => {
          toast.success(`Inserted successfully`);
          setSaveIsLoading(false);
          handleClose();
          if (reloadStatuses) reloadStatuses();
        })
        .catch((error) => {
          setSaveIsLoading(false);
          toast.error(`Error While Saving`);
        });
    }
  };

  useEffect(() => {
    resetForm();
    if (statuses.length === 0 && visible) getStatuses();
  }, [visible, jobId]);

  return (
    <Dialog
      open={visible}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 12,
          minHeight: 400
        }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 3, 
        pb: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          {formData.id > 0 ? "Update Status" : "Add New Status"}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
            '&:hover': {
              backgroundColor: (theme) => theme.palette.grey[100]
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 250 }}>
              <SingleCustomAutocomplete
                filedName="status_id"
                label="Status"
                list={statuses}
                value={formData.status_id}
                listKey="id"
                description="name"
                customOnChange={customOnChange}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 250 }}>
              <TextField
                fullWidth
                type="date"
                label="Follow up Date"
                id="date"
                value={formData.date}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Box>
          </Box>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Description"
            id="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Enter status description..."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1, gap: 2 }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            px: 3
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={saveForm}
          variant="contained"
          disabled={saveIsLoading}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            minWidth: 100
          }}
        >
          {saveIsLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            formData.id > 0 ? "Update" : "Save"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusAddUpdateForm; 